import { Box, Button, useTheme } from "@mui/material";
import { RecordingCard } from "@pages:app/audio/components/RecordingCard";
import { Waveform } from "@pages:app/audio/components/Waveform";
import { MicrophoneRecorder, useElapsedSeconds } from "@pages:app/audio/hooks";
import { formatClock } from "@pages:app/audio/util";
import { useTranslation } from "react-i18next";
import { MdMic, MdStop } from "react-icons/md";

const STRIP_BUCKETS = 160;

interface RecorderCardProps {
  recorder: MicrophoneRecorder;
  onRecorded: (file: File) => void;
}

/*
 * The microphone take while it runs: the strip fills from the left with the
 * loudness so far, and the clock counts. Stop turns it into a file.
 */
export const RecorderCard = ({ recorder, onRecorded }: RecorderCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const elapsed = useElapsedSeconds(recorder.startedAt);
  const recorded = recorder.levels.length;
  const peaks = [
    ...recorder.levels,
    ...new Array(Math.max(0, STRIP_BUCKETS - recorded)).fill(0),
  ];
  const barColor = (index: number) =>
    index < recorded ? theme.palette.primary.main : null;

  const stop = async () => {
    const file = await recorder.stop();
    if (file) onRecorded(file);
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <RecordingCard
        file={null}
        name={t("audio.record.inProgress")}
        meta={formatClock(elapsed)}
        icon={<MdMic size={28} />}
        top={<Waveform peaks={peaks} barColor={barColor} />}
      >
        <Button
          variant="outlined"
          startIcon={<MdStop />}
          onClick={stop}
          disabled={recorder.status !== "recording"}
        >
          {t("audio.record.stop")}
        </Button>
      </RecordingCard>
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Button variant="text" size="small" onClick={recorder.cancel}>
          {t("audio.record.cancel")}
        </Button>
      </Box>
    </Box>
  );
};
